import { type RuleCompletionInfo, RuleType } from '@fruitful/types';
import { PiggyBankIcon, TreePalmIcon, UmbrellaIcon } from 'lucide-react-native';
import { colorPrimitiveTokens, type ColorTokenKey } from 'smoothie';

export const toHex = (token: ColorTokenKey): string =>
    (colorPrimitiveTokens as Record<string, string>)[token.slice(1)] ?? token;

const MONTH_NAMES: Record<string, number> = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
};

export const parseDate = (dateStr: string): Date => {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    const withSlashes = new Date(dateStr.replace(/-/g, '/'));
    if (!isNaN(withSlashes.getTime())) return withSlashes;
    const withDay = new Date(`1 ${dateStr}`);
    if (!isNaN(withDay.getTime())) return withDay;
    // Handle "Month Year" formats like "Oct 2026" that Hermes can't parse
    const parts = dateStr.trim().split(/\s+/);
    if (parts.length === 2) {
        const monthIdx = MONTH_NAMES[parts[0]?.toLowerCase() ?? ''];
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const year = parseInt(parts[1]!, 10);
        if (monthIdx != null && !isNaN(year)) {
            return new Date(year, monthIdx, 1);
        }
    }
    return d;
};

export const INCOME_BG: ColorTokenKey = '$lemon';
export const INCOME_TEXT = '$lemonDarkest';
export const BILLS_COLOR: ColorTokenKey = '$water';
export const SPEND_COLOR: ColorTokenKey = '$leaf';
export const GOALS_COLOR: ColorTokenKey = '$petal';

export const INCOME_SYSTEM_BG: ColorTokenKey = '$lemonLight';
export const BILLS_SYSTEM_COLOR: ColorTokenKey = '$waterLight';
export const SPEND_SYSTEM_COLOR: ColorTokenKey = '$leafLight';
export const GOALS_SYSTEM_COLOR: ColorTokenKey = '$petalLight';

export const MAX_BAR_HEIGHT = 160;
export const MIN_BAR_HEIGHT = 56;
export const PATHS_HEIGHT = 120;
export const BAR_GAP = 24;
export const BAR_RADIUS = 16;
export const STROKE_WIDTH = 12;
export const CURVE_TO_BAR_GAP = 8;
export const PILL_HEIGHT = 56;
export const CONNECTOR_LINE_HEIGHT = 32;
export const LOGO_BOX_SIZE = 48;
export const LOGO_GAP = 4;
export const PILL_TO_CURVES_GAP =
    LOGO_GAP + CONNECTOR_LINE_HEIGHT + LOGO_GAP + LOGO_BOX_SIZE + LOGO_GAP;

export const SYSTEM_GAP = 12;
export const STEP_DURATION = 300;
export const DEFAULT_SYSTEM_HEIGHT = 60;

export interface GoalIconPosition {
    x: number;
    y: number;
    type: RuleType;
}

export const GOALS_BG_LIGHT = toHex('$petalLightest');
export const GOALS_BG_DARK = toHex('$petalLight');
export const GOALS_LINE = toHex('$petal');
export const GOALS_ICONS = toHex('$petal');
export const GOALS_LABEL = toHex('$petalDark');
export const DEBT_BG_LIGHT = toHex('$peachLightest');
export const DEBT_LINE = toHex('$peach');
export const DEBT_BG_DARK = toHex('$peachLight');
export const DEBT_ICONS = toHex('$peach');
export const DEBT_LABEL = toHex('$peachDark');
export const GOALS_FILLED = toHex('$petalDarkest');

export const getRuleIcon = (type: RuleType) => {
    switch (type) {
        case RuleType.DebtPayDown:
            return PiggyBankIcon;
        case RuleType.Retirement:
            return TreePalmIcon;
        case RuleType.StarterEmergencyFund:
        case RuleType.FullEmergencyFund:
            return UmbrellaIcon;
        case RuleType.Savings:
            return PiggyBankIcon;
        default:
            return PiggyBankIcon;
    }
};

export const getRuleColors = (
    type: RuleType,
): { bg: string; dark: string; lightest: string } => {
    if (type === RuleType.DebtPayDown) {
        return { bg: DEBT_LINE, dark: DEBT_LABEL, lightest: DEBT_BG_LIGHT };
    }
    return { bg: GOALS_LINE, dark: GOALS_LABEL, lightest: GOALS_BG_LIGHT };
};

export const computeDurationLabel = (
    completionDate: string | null,
): string | null => {
    if (completionDate == null) return null;
    const parsed = parseDate(completionDate);
    if (isNaN(parsed.getTime())) return null;
    const now = new Date();
    let months =
        (parsed.getFullYear() - now.getFullYear()) * 12 +
        (parsed.getMonth() - now.getMonth());
    if (months < 1) months = 1;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${remainingMonths} MO`;
    if (remainingMonths === 0) return `${years} YR`;
    return `${years} YR ${remainingMonths} MO`;
};

export const splitCompletionDate = (
    completionLabel: string | null,
): { month: string; year: string } | null => {
    if (completionLabel == null) return null;
    const parts = completionLabel.split(' ');
    if (parts.length < 2) return null;
    return { month: parts[0] ?? '', year: parts[1] ?? '' };
};

export const GRAPH_PADDING_LEFT = 0;
export const GRAPH_PADDING_RIGHT = 0;
export const GRAPH_PADDING_TOP = 48;
export const GRAPH_PADDING_BOTTOM = 44;
export const GRAPH_HEIGHT = 246;
export const ICON_SIZE = 24;
export const LINE_DRAW_DURATION = 400;

export interface DebtGraphData {
    totalCents: number;
    payoffMonths: number;
    annualRate: number;
}

const MAX_PAYOFF_MONTHS = 360;

export const getDebtData = (
    rules: RuleCompletionInfo[],
    monthlyGoalsCents?: number,
): DebtGraphData | null => {
    const debtRule = rules.find((r) => r.type === RuleType.DebtPayDown);
    if (debtRule?.targetBalanceCents == null) return null;
    if (debtRule.targetBalanceCents <= 0) return null;

    let annualRate = 0.18;
    const subs = debtRule.subAccounts;
    if (subs != null && subs.length > 0) {
        const totalBalance = subs.reduce((s, a) => s + a.balanceCents, 0);
        if (totalBalance > 0) {
            annualRate =
                subs.reduce(
                    (s, a) => s + a.annualInterestRate * a.balanceCents,
                    0,
                ) / totalBalance;
        }
    }

    let payoffMonths = 12;
    if (debtRule.completionDate != null) {
        const parsed = parseDate(debtRule.completionDate);
        if (!isNaN(parsed.getTime())) {
            const now = new Date();
            payoffMonths = Math.max(
                1,
                (parsed.getFullYear() - now.getFullYear()) * 12 +
                    (parsed.getMonth() - now.getMonth()),
            );
        }
    } else if (monthlyGoalsCents != null && monthlyGoalsCents > 0) {
        const monthlyPayment = monthlyGoalsCents * debtRule.splitPercentage;
        const r = annualRate / 12;
        if (r > 0.0001 && monthlyPayment > debtRule.targetBalanceCents * r) {
            payoffMonths = Math.min(
                MAX_PAYOFF_MONTHS,
                Math.ceil(
                    -Math.log(
                        1 - (debtRule.targetBalanceCents * r) / monthlyPayment,
                    ) / Math.log(1 + r),
                ),
            );
        } else {
            payoffMonths = MAX_PAYOFF_MONTHS;
        }
    }

    return {
        totalCents: debtRule.targetBalanceCents,
        payoffMonths,
        annualRate,
    };
};

export interface TimeAxisConfig {
    totalMonths: number;
    labels: string[];
}

export const computeTimeAxisMonths = (
    debtPayoffMonths: number | null,
    allRules: RuleCompletionInfo[],
): number => {
    return computeTimeAxis(debtPayoffMonths, allRules).totalMonths;
};

export const computeTimeAxis = (
    debtPayoffMonths: number | null,
    allRules: RuleCompletionInfo[],
): TimeAxisConfig => {
    let maxMonths = debtPayoffMonths ?? 0;

    for (const rule of allRules) {
        if (rule.completionDate != null) {
            const parsed = parseDate(rule.completionDate);
            if (!isNaN(parsed.getTime())) {
                const now = new Date();
                const months =
                    (parsed.getFullYear() - now.getFullYear()) * 12 +
                    (parsed.getMonth() - now.getMonth());
                if (months > maxMonths) maxMonths = months;
            }
        }
    }

    if (maxMonths <= 0) maxMonths = 36;

    // ≤ 12 months: TODAY → 6 MO → 12 MO
    if (maxMonths <= 12) {
        return {
            totalMonths: 12,
            labels: ['TODAY', '6 MO', '12 MO'],
        };
    }

    // ≤ 24 months: TODAY → 6 MO → 12 MO → 18 MO → 2 YR
    if (maxMonths <= 24) {
        return {
            totalMonths: 24,
            labels: ['TODAY', '6 MO', '12 MO', '18 MO', '2 YR'],
        };
    }

    // > 24 months: existing logic with year steps
    let yearStep = Math.max(1, Math.ceil(maxMonths / 12 / 3));
    const padding = yearStep * 2;
    yearStep = Math.max(1, Math.ceil((maxMonths + padding) / 12 / 3));
    const totalMonths = yearStep * 3 * 12;

    const labels: string[] = ['TODAY'];
    for (let i = 1; i <= 3; i++) {
        labels.push(`${yearStep * i} YR`);
    }

    return { totalMonths, labels };
};

export const computeAmortizationPoints = (
    startBalance: number,
    annualRate: number,
    totalMonths: number,
    numPoints: number,
): number[] => {
    const r = annualRate / 12;

    if (r < 0.0001) {
        return Array.from({ length: numPoints }, (_, i) => {
            const t = i / (numPoints - 1);
            return startBalance * (1 - t);
        });
    }

    const rN = (1 + r) ** totalMonths;
    const payment = (startBalance * r * rN) / (rN - 1);

    return Array.from({ length: numPoints }, (_, i) => {
        const month = (i / (numPoints - 1)) * totalMonths;
        const rn = (1 + r) ** month;
        const balance = startBalance * rn - payment * ((rn - 1) / r);
        return Math.max(0, balance);
    });
};

export const Y_AXIS_HEADROOM = 1.2;

export const catmullRomToBezier = (
    points: Array<{ x: number; y: number }>,
    tension = 0.5,
): string => {
    if (points.length < 2) return '';
    const pt = (idx: number) => points[idx] ?? { x: 0, y: 0 };
    let d = `M ${pt(0).x} ${pt(0).y}`;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = pt(Math.max(0, i - 1));
        const p1 = pt(i);
        const p2 = pt(i + 1);
        const p3 = pt(Math.min(points.length - 1, i + 2));
        const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
        const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
        const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
        const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;
        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return d;
};

export const buildAmortizationPoints = (
    graphW: number,
    graphH: number,
    payoffFraction: number,
    balancePoints: number[],
    yMax: number,
): Array<{ x: number; y: number }> => {
    const endX = payoffFraction * graphW;
    const count = balancePoints.length;
    const toX = (i: number) => (i / (count - 1)) * endX;
    const toY = (bal: number) => graphH * (1 - bal / yMax);
    return balancePoints.map((bal, i) => ({
        x: toX(i),
        y: toY(bal ?? 0),
    }));
};

export const buildAmortizationPath = (
    graphW: number,
    graphH: number,
    payoffFraction: number,
    balancePoints: number[],
    yMax: number,
): string =>
    catmullRomToBezier(
        buildAmortizationPoints(
            graphW,
            graphH,
            payoffFraction,
            balancePoints,
            yMax,
        ),
    );

export const buildAmortizationFillPath = (
    graphW: number,
    graphH: number,
    payoffFraction: number,
    balancePoints: number[],
    yMax: number,
): string => {
    const pts = buildAmortizationPoints(
        graphW,
        graphH,
        payoffFraction,
        balancePoints,
        yMax,
    );
    if (pts.length === 0) return '';
    const endX = payoffFraction * graphW;
    let d = `M ${pts[0]?.x ?? 0} ${pts[0]?.y ?? 0}`;
    for (let i = 1; i < pts.length; i++) {
        const p = pts[i] ?? { x: 0, y: 0 };
        d += ` L ${p.x} ${p.y}`;
    }
    d += ` L ${endX} ${graphH} L 0 ${graphH} Z`;
    return d;
};

export const AMORTIZATION_POINTS = 60;

export interface GoalGraphData {
    monthlyContributionCents: number;
    completionMonths: number | null;
    type: RuleType;
}

export const getGoalsLineData = (
    rules: RuleCompletionInfo[],
    debtData: DebtGraphData | null,
): {
    phase1MonthlyCents: number;
    phase2MonthlyCents: number;
    goals: GoalGraphData[];
} => {
    const nonDebtRules = rules.filter((r) => r.type !== RuleType.DebtPayDown);

    const phase1MonthlyCents = nonDebtRules.reduce((sum, r) => {
        if (r.monthlyContributionMaxCents != null)
            return sum + r.monthlyContributionMaxCents;
        if (r.yearlyContributionMaxCents != null)
            return sum + Math.round(r.yearlyContributionMaxCents / 12);
        return sum;
    }, 0);

    let debtMonthlyCents = 0;
    if (debtData != null) {
        const debtRule = rules.find((r) => r.type === RuleType.DebtPayDown);
        if (debtRule?.monthlyContributionMaxCents != null) {
            debtMonthlyCents = debtRule.monthlyContributionMaxCents;
        } else if (debtRule?.yearlyContributionMaxCents != null) {
            debtMonthlyCents = Math.round(
                debtRule.yearlyContributionMaxCents / 12,
            );
        } else {
            const r = debtData.annualRate / 12;
            if (r > 0.0001) {
                const rN = (1 + r) ** debtData.payoffMonths;
                debtMonthlyCents = Math.round(
                    (debtData.totalCents * r * rN) / (rN - 1),
                );
            } else {
                debtMonthlyCents = Math.round(
                    debtData.totalCents / debtData.payoffMonths,
                );
            }
        }
    }

    const goals: GoalGraphData[] = nonDebtRules.map((r) => {
        let monthly = 0;
        if (r.monthlyContributionMaxCents != null)
            monthly = r.monthlyContributionMaxCents;
        else if (r.yearlyContributionMaxCents != null)
            monthly = Math.round(r.yearlyContributionMaxCents / 12);

        let completionMonths: number | null = null;
        if (r.completionDate != null) {
            const parsed = parseDate(r.completionDate);
            if (!isNaN(parsed.getTime())) {
                const now = new Date();
                completionMonths = Math.max(
                    1,
                    (parsed.getFullYear() - now.getFullYear()) * 12 +
                        (parsed.getMonth() - now.getMonth()),
                );
            }
        }

        return {
            monthlyContributionCents: monthly,
            completionMonths,
            type: r.type,
        };
    });

    return {
        phase1MonthlyCents,
        phase2MonthlyCents: phase1MonthlyCents + debtMonthlyCents,
        goals,
    };
};

export const goalsValueAtMonth = (
    month: number,
    phase1Monthly: number,
    phase2Monthly: number,
    debtPayoffMonths: number | null,
): number => {
    if (debtPayoffMonths == null || month <= debtPayoffMonths) {
        return phase1Monthly * month;
    }
    const phase1Total = phase1Monthly * debtPayoffMonths;
    return phase1Total + phase2Monthly * (month - debtPayoffMonths);
};

export const computeTimelineIconTargets = (
    allRules: RuleCompletionInfo[],
    containerWidth: number,
    monthlyGoalsCents?: number,
): GoalIconPosition[] => {
    if (containerWidth <= 0) return [];
    const debtData = getDebtData(allRules, monthlyGoalsCents);
    const totalAxisMonths = computeTimeAxisMonths(
        debtData?.payoffMonths ?? null,
        allRules,
    );
    const goalsLine = getGoalsLineData(allRules, debtData);
    const graphW = containerWidth - GRAPH_PADDING_LEFT - GRAPH_PADDING_RIGHT;
    const graphH = GRAPH_HEIGHT - GRAPH_PADDING_TOP - GRAPH_PADDING_BOTTOM;
    const debtMax = debtData?.totalCents ?? 0;
    const goalsMax = goalsValueAtMonth(
        totalAxisMonths,
        goalsLine.phase1MonthlyCents,
        goalsLine.phase2MonthlyCents,
        debtData?.payoffMonths ?? null,
    );
    const yMax = Math.max(debtMax, goalsMax, 1) * Y_AXIS_HEADROOM;

    const targets: GoalIconPosition[] = [];

    for (const g of goalsLine.goals) {
        if (g.completionMonths == null) continue;
        if (g.completionMonths > totalAxisMonths) continue;
        const val = goalsValueAtMonth(
            g.completionMonths,
            goalsLine.phase1MonthlyCents,
            goalsLine.phase2MonthlyCents,
            debtData?.payoffMonths ?? null,
        );
        targets.push({
            x:
                GRAPH_PADDING_LEFT +
                (g.completionMonths / totalAxisMonths) * graphW,
            y: GRAPH_PADDING_TOP + graphH * (1 - val / yMax),
            type: g.type,
        });
    }

    if (debtData != null) {
        const debtPayoffFraction = debtData.payoffMonths / totalAxisMonths;
        targets.push({
            x: GRAPH_PADDING_LEFT + debtPayoffFraction * graphW,
            y: GRAPH_PADDING_TOP + graphH,
            type: RuleType.DebtPayDown,
        });
    }

    return targets;
};

export const buildGoalsLinePoints = (
    graphW: number,
    graphH: number,
    totalAxisMonths: number,
    yMax: number,
    phase1Monthly: number,
    phase2Monthly: number,
    debtPayoffMonths: number | null,
): Array<{ x: number; y: number }> => {
    const toX = (month: number) => (month / totalAxisMonths) * graphW;
    const toY = (cents: number) => graphH * (1 - cents / yMax);

    const SAMPLE_COUNT = 40;
    const points: Array<{ x: number; y: number }> = [];
    for (let i = 0; i <= SAMPLE_COUNT; i++) {
        const month = (i / SAMPLE_COUNT) * totalAxisMonths;
        const val = goalsValueAtMonth(
            month,
            phase1Monthly,
            phase2Monthly,
            debtPayoffMonths,
        );
        points.push({ x: toX(month), y: toY(val) });
    }
    return points;
};

export const buildGoalsLinePath = (
    graphW: number,
    graphH: number,
    totalAxisMonths: number,
    yMax: number,
    phase1Monthly: number,
    phase2Monthly: number,
    debtPayoffMonths: number | null,
): string =>
    catmullRomToBezier(
        buildGoalsLinePoints(
            graphW,
            graphH,
            totalAxisMonths,
            yMax,
            phase1Monthly,
            phase2Monthly,
            debtPayoffMonths,
        ),
    );

export const buildGoalsFillPath = (
    graphW: number,
    graphH: number,
    totalAxisMonths: number,
    yMax: number,
    phase1Monthly: number,
    phase2Monthly: number,
    debtPayoffMonths: number | null,
): string => {
    const pts = buildGoalsLinePoints(
        graphW,
        graphH,
        totalAxisMonths,
        yMax,
        phase1Monthly,
        phase2Monthly,
        debtPayoffMonths,
    );
    if (pts.length === 0) return '';
    let d = `M ${pts[0]?.x ?? 0} ${pts[0]?.y ?? 0}`;
    for (let i = 1; i < pts.length; i++) {
        const p = pts[i] ?? { x: 0, y: 0 };
        d += ` L ${p.x} ${p.y}`;
    }
    d += ` L ${graphW} ${graphH} L 0 ${graphH} Z`;
    return d;
};

export const ICON_SELECTED_SIZE = 32;

export const LABEL_SLIDE_DURATION = 150;
export const LABEL_SLIDE_DISTANCE = 16;
export const BG_FADE_DURATION = 200;

export const CONTENT_STAGGER_DELAY = 100;
export const CONTENT_SLIDE_DURATION = 300;
export const CONTENT_SLIDE_DISTANCE = 20;
